import React, { useEffect, useRef, useState } from "react";
import { FiTag } from "react-icons/fi";
import { useSupportTags } from "./useSupportVocabulary";

/**
 * Tag editor for the ticket header.
 *
 * The ticket `PATCH` **replaces** the whole tag set, so every change sends the
 * full list rather than a delta. Unknown names are created for the company
 * automatically, which is why a free-text entry is offered alongside the
 * existing vocabulary.
 *
 * @param {Object} props
 * @param {string[]} props.tags - Tags currently on the ticket.
 * @param {(tags: string[]) => void} props.onChange - Receives the complete new list.
 * @param {boolean} [props.disabled]
 */
export function TagEditor({ tags, onChange, disabled = false }) {
  const { tags: vocabulary, reload } = useSupportTags();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  const toggle = (name) => {
    const next = tags.includes(name)
      ? tags.filter((t) => t !== name)
      : [...tags, name];
    onChange(next);
  };

  const addDraft = (e) => {
    e.preventDefault();
    const name = draft.trim();
    if (!name || tags.includes(name)) {
      setDraft("");
      return;
    }
    onChange([...tags, name]);
    setDraft("");
    // A brand-new name joins the company vocabulary server-side.
    reload();
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Edit tags"
        className="rounded-lg border border-gray-200 p-1.5 text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <FiTag size={16} />
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
          <div className="max-h-48 space-y-0.5 overflow-y-auto">
            {vocabulary.length === 0 && tags.length === 0 ? (
              <p className="px-1 py-2 text-xs text-gray-400">No tags yet.</p>
            ) : (
              // The ticket's own tags first, so a tag not in the vocabulary
              // (created ad hoc) is still removable.
              Array.from(new Set([...tags, ...vocabulary.map((t) => t.name)]))
                .filter(Boolean)
                .map((name) => (
                  <label
                    key={name}
                    className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={tags.includes(name)}
                      onChange={() => toggle(name)}
                      className="h-3.5 w-3.5 accent-orange-primary"
                    />
                    {name}
                  </label>
                ))
            )}
          </div>

          <form onSubmit={addDraft} className="mt-2 flex gap-1 border-t border-gray-100 pt-2">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="New tag"
              aria-label="New tag"
              className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-orange-primary/40"
            />
            <button
              type="submit"
              className="rounded bg-orange-primary px-2 py-1 text-xs font-semibold text-white hover:brightness-95"
            >
              Add
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default TagEditor;
